/** @format */

import React from "react";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import {
	Download,
	Check,
	HelpCircle,
	Phone,
	AlertTriangle,
	MoveHorizontal,
	MoveVertical,
	Ruler,
} from "lucide-react"; // Added specific icons

// Components
import ContactBlock from "../../components/contact/ContactBlock"; // Assuming path
// Removed ValanceItem/ValanceOptions import - keep this page focused on measuring

// Data (PDF Path)
import MeasuringPDF from "../../pdf/OCS - Order Preparation Form.pdf"; // Assuming path

const MeasuringGuideScreen = () => {
	// Animation variant for sections
	const sectionVariants = {
		hidden: { opacity: 0, y: 30 },
		visible: {
			opacity: 1,
			y: 0,
			transition: { duration: 0.5, ease: "easeOut" },
		},
	};

	// Simple structure for guide steps
	const guideSteps = [
		// Step 1: Tools Needed
		{
			id: "tools",
			title: "Tools You'll Need",
			icon: Ruler,
			content: (
				<div className="space-y-4">
					<p className="text-gray-700">
						Measuring accurately is simple! You just need a few basic tools:
					</p>
					<ul className="list-disc list-outside space-y-2 pl-5 text-gray-700">
						<li>
							<strong>Metal Tape Measure:</strong> Essential for accuracy.
							Fabric tapes can stretch.
						</li>
						<li>
							<strong>Pencil & Paper:</strong> Or use our handy worksheet
							(download below).
						</li>
						<li>
							<strong>Step Ladder (Optional):</strong> For taller windows.
						</li>
					</ul>
					<div className="!mt-6 bg-yellow-50 border border-yellow-200 text-yellow-800 p-3 rounded-lg text-sm flex items-start gap-2">
						<AlertTriangle
							size={18}
							className="mt-0.5 text-yellow-600 flex-shrink-0"
						/>
						<span>
							<strong>Important:</strong> Measure each window individually, even
							if they look the same size. Slight variations are common.
						</span>
					</div>
				</div>
			),
		},
		// Step 2: Download Worksheet
		{
			id: "worksheet",
			title: "Download Measuring Worksheet",
			icon: Download,
			content: (
				<div className="text-center">
					<p className="text-gray-700 mb-5">
						Keep your measurements organized. Download and print our simple
						worksheet to record width and height for each window.
					</p>
					<motion.a
						href={MeasuringPDF}
						target="_blank"
						rel="noopener noreferrer" // Add rel for security
						className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-white rounded-lg font-semibold shadow-md hover:bg-primary/90 transition-colors focus:outline-none focus:ring-2 focus:ring-primary/50 focus:ring-offset-2"
						whileHover={{ scale: 1.03 }}
						whileTap={{ scale: 0.98 }}
					>
						<Download size={18} /> Download Worksheet (PDF)
					</motion.a>
				</div>
			),
		},
		// Step 3: Choose Mount Type
		{
			id: "mount-type",
			title: "Decide: Inside or Outside Mount?",
			icon: HelpCircle,
			content: (
				<div className="space-y-6">
					<p className="text-gray-700">
						This is the most important decision before measuring. It determines
						where your shade will hang and how you take measurements.
					</p>
					<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
						{/* Inside Mount Card */}
						<div className="border border-gray-200 rounded-lg p-5 bg-white space-y-3">
							<img
								src="/images/windows/guide/mount_inside.png"
								alt="Inside Mount Diagram"
								className="w-full rounded-md shadow-sm mb-3 aspect-video object-contain"
							/>
							<h3 className="font-semibold text-lg text-gray-800">
								Inside Mount
							</h3>
							<p className="text-sm text-gray-600">
								Shade fits *within* the window frame for a clean, built-in look.
							</p>
							<ul className="list-disc list-outside space-y-1 pl-5 text-sm text-gray-600">
								<li>
									Requires sufficient frame depth (usually min. 2", check
									product specs).
								</li>
								<li>May have small light gaps on the sides.</li>
								<li>Measure the exact opening width & height.</li>
							</ul>
							<p className="text-xs text-blue-600 pt-1">
								We make a small deduction (~1/8") from your width for clearance.
							</p>
						</div>
						{/* Outside Mount Card */}
						<div className="border border-gray-200 rounded-lg p-5 bg-white space-y-3">
							<img
								src="/images/windows/guide/mount_outside.png"
								alt="Outside Mount Diagram"
								className="w-full rounded-md shadow-sm mb-3 aspect-video object-contain"
							/>
							<h3 className="font-semibold text-lg text-gray-800">
								Outside Mount
							</h3>
							<p className="text-sm text-gray-600">
								Shade mounts on the wall or trim *above and around* the window
								frame.
							</p>
							<ul className="list-disc list-outside space-y-1 pl-5 text-sm text-gray-600">
								<li>Ideal for shallow frames or covering imperfections.</li>
								<li>Provides better light blockage (minimal gaps).</li>
								<li>
									Measure the desired width & height you want the *shade* to be.
								</li>
							</ul>
							<p className="text-xs text-blue-600 pt-1">
								We make the shade to the exact dimensions you provide.
							</p>
						</div>
					</div>
					{/* <p className="text-sm text-gray-700 !mt-5">
                        **Valance Note:** For Inside Mount, ensure your window frame depth can accommodate your chosen valance/cassette. Check valance depths on the product page or <Link to="/guides/valances" className="text-primary hover:underline">valance guide</Link>.
                     </p> */}
				</div>
			),
		},
		// Step 4: How to Measure (Inside Mount)
		{
			id: "measure-inside",
			title: "How to Measure for Inside Mount",
			icon: MoveHorizontal, // Or specific inside icon
			content: (
				<div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 items-center">
					{/* Diagram */}
					<div className="order-1">
						<img
							src="/images/guide/measure_inside_detailed.jpg"
							alt="Measuring for Inside Mount"
							className="w-full rounded-lg shadow-md"
						/>
						<p className="text-xs text-center text-gray-500 mt-2">
							Measure width at top, middle, bottom. Measure height at left,
							middle, right.
						</p>
					</div>
					{/* Instructions */}
					<div className="order-2 space-y-4">
						<h4 className="font-semibold text-gray-800">
							Measuring Width (Inside Mount):
						</h4>
						<ul className="list-decimal list-outside space-y-2 pl-5 text-gray-700 text-sm">
							<li>
								Using a metal tape measure, measure the inside width of the
								window frame at the{" "}
								<strong className="text-primary">top, middle,</strong> and{" "}
								<strong className="text-primary">bottom</strong>.
							</li>
							<li>
								Record the <strong className="text-primary">narrowest</strong>{" "}
								(smallest) of these three width measurements. Round down to the
								nearest 1/8 inch if needed.
							</li>
							<li>
								This narrowest width is what you'll enter when ordering.{" "}
								<strong className="text-primary">Do NOT make deductions</strong>{" "}
								– we will make a small deduction (~1/8") for clearance.
							</li>
						</ul>
						<h4 className="font-semibold text-gray-800 pt-2">
							Measuring Height (Inside Mount):
						</h4>
						<ul className="list-decimal list-outside space-y-2 pl-5 text-gray-700 text-sm">
							<li>
								Measure the inside height from the top inside edge of the frame
								down to the window sill (or where you want the shade to rest) at
								the <strong className="text-primary">left, middle,</strong> and{" "}
								<strong className="text-primary">right</strong>.
							</li>
							<li>
								Record the <strong className="text-primary">shortest</strong> of
								these three height measurements. Round down to the nearest 1/8
								inch if needed.
							</li>
							<li>This shortest height is what you'll enter when ordering.</li>
						</ul>
						<h4 className="font-semibold text-gray-800 pt-2">Check Depth:</h4>
						<p className="text-sm text-gray-700">
							Ensure your window frame has enough flat depth (usually min 2") to
							mount the brackets. Check the specific product page for
							requirements, especially if using a cassette.
						</p>
					</div>
				</div>
			),
		},
		// Step 5: How to Measure (Outside Mount)
		{
			id: "measure-outside",
			title: "How to Measure for Outside Mount",
			icon: MoveVertical, // Or specific outside icon
			content: (
				<div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 items-center">
					{/* Diagram */}
					<div className="order-1">
						<img
							src="/images/guide/measure_outside_detailed.jpg"
							alt="Measuring for Outside Mount"
							className="w-full rounded-lg shadow-md"
						/>
						<p className="text-xs text-center text-gray-500 mt-2">
							Measure the exact width and height you want the finished shade to
							be.
						</p>
					</div>
					{/* Instructions */}
					<div className="order-2 space-y-4">
						<h4 className="font-semibold text-gray-800">
							Measuring Width (Outside Mount):
						</h4>
						<ul className="list-decimal list-outside space-y-2 pl-5 text-gray-700 text-sm">
							<li>
								Decide how much you want the shade to overlap the window opening
								on each side. We recommend at least{" "}
								<strong className="text-primary">
									1.5 to 3 inches per side
								</strong>{" "}
								(total 3-6 inches wider than the opening) for better light
								control and privacy.
							</li>
							<li>
								Measure the desired{" "}
								<strong className="text-primary">total width</strong> where you
								want the shade (including overlap) to cover.
							</li>
							<li>
								This total width is what you'll enter when ordering.{" "}
								<strong className="text-primary">
									We will make the shade exactly this wide.
								</strong>
							</li>
						</ul>
						<h4 className="font-semibold text-gray-800 pt-2">
							Measuring Height (Outside Mount):
						</h4>
						<ul className="list-decimal list-outside space-y-2 pl-5 text-gray-700 text-sm">
							<li>
								Determine where the top of the shade (valance/cassette or
								headrail) will be mounted. Ensure you have enough flat surface
								above the frame (usually min 2 inches).
							</li>
							<li>
								Measure from that top mounting point down to where you want the
								bottom of the shade to hang (e.g., the window sill, or slightly
								below for better coverage).
							</li>
							<li>
								This total height is what you'll enter when ordering.{" "}
								<strong className="text-primary">
									We will make the shade exactly this tall.
								</strong>
							</li>
						</ul>
						<h4 className="font-semibold text-gray-800 pt-2">Tip:</h4>
						<p className="text-sm text-gray-700">
							Consider adding extra height if mounting well above the window to
							ensure coverage when the shade is fully lowered.
						</p>
					</div>
				</div>
			),
		},
		// Step 6: Final Check & Order
		{
			id: "final-check",
			title: "Final Check & Ordering",
			icon: Check,
			content: (
				<div className="space-y-4">
					<p className="text-gray-700">
						Double-check your measurements before ordering!
					</p>
					<ul className="list-disc list-outside space-y-2 pl-5 text-gray-700 text-sm">
						<li>Did you use a metal tape measure?</li>
						<li>Did you measure each window individually?</li>
						<li>
							Did you record the correct width/height based on Inside or Outside
							mount? (Narrowest/Shortest for Inside, Desired Finished Size for
							Outside)
						</li>
						<li>Did you record measurements as Width x Height?</li>
						<li>
							For Inside Mount, did you check your frame depth against product
							requirements?
						</li>
					</ul>
					<p className="text-gray-700 pt-2">
						Once you have your measurements, head over to the product pages to
						start customizing!
					</p>
					<Link
						to="/shop"
						className="inline-flex items-center gap-2 px-6 py-3 bg-secondary text-white rounded-lg font-semibold shadow-md hover:bg-secondary/90 transition-colors"
					>
						Shop All Shades
					</Link>
				</div>
			),
		},
	];

	return (
		<div className="overflow-x-hidden bg-gradient-to-b from-white via-gray-50 to-gray-100">
			<Helmet>
				<title>
					How to Measure for Window Shades & Blinds | Off Cut Shades Guide
				</title>
				<meta
					name="description"
					content="Learn the simple steps to accurately measure your windows for inside or outside mount custom shades and blinds. Download our free worksheet. Get expert help from Off Cut Shades."
				/>
				<link
					rel="canonical"
					href="https://offcutshades.com/guides/measuring-guide"
				/>{" "}
				{/* Updated canonical */}
			</Helmet>
			<main className="container mx-auto px-4 pt-24 pb-16 md:pt-32 md:pb-24">
				{" "}
				{/* Added more top padding */}
				{/* --- Hero Section --- */}
				<motion.div
					className="text-center mb-12 md:mb-16"
					initial={{ opacity: 0, y: 20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.5, ease: "easeOut" }}
				>
					{/* Icon instead of Image */}
					<motion.div
						className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 text-primary mb-4"
						initial={{ scale: 0.8, opacity: 0 }}
						animate={{ scale: 1, opacity: 1 }}
						transition={{ delay: 0.1, type: "spring", stiffness: 150 }}
					>
						<Ruler size={36} strokeWidth={1.5} />
					</motion.div>

					<h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-3">
						Measure for Your Perfect Shades
					</h1>
					<p className="text-lg text-gray-600 max-w-2xl mx-auto">
						Follow these simple steps for accurate measurements and a perfect
						fit, every time.
					</p>
				</motion.div>
				{/* --- Measurement Steps --- */}
				<div className="space-y-8 md:space-y-12 max-w-4xl mx-auto">
					{" "}
					{/* Constrained width */}
					{guideSteps.map((step, index) => (
						<motion.section
							key={step.id}
							id={step.id} // Add ID for potential internal links
							className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden"
							variants={sectionVariants}
							initial="hidden"
							whileInView="visible"
							viewport={{ once: true, amount: 0.2 }} // Trigger when 20% visible
						>
							{/* Step Header */}
							<div className="p-5 md:p-6 border-b border-gray-100">
								<h2 className="text-xl md:text-2xl font-semibold text-gray-800 flex items-center gap-3">
									{step.icon &&
										React.createElement(step.icon, {
											size: 24,
											className: "text-primary flex-shrink-0",
										})}
									<span>{step.title}</span>
								</h2>
							</div>
							{/* Step Content */}
							<div className="p-5 md:p-6 md:pt-5">{step.content}</div>
						</motion.section>
					))}
				</div>
			</main>
			{/* --- Contact Support Section --- */}
			<ContactBlock /> {/* Use existing component */}
		</div>
	);
};

export default MeasuringGuideScreen;
